import * as errors from '../errors'
import { userAuthHeader } from '../apiClient'
import type { IServiceError } from '../errors'
import {
  newRequestId,
  fetchOrThrowNetworkError,
  readErrorBody,
  parseErrorResponse,
} from './utils'
import { HTTPStatus } from '~/api/httpStatus'
import { handleAsyncDataErrors } from '~/cynqueModules/handleAsyncDataErrors'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import { getNuxtContext } from '~/cynqueModules/nuxtRuntime'

interface DataResponse<D> {
  data: D
}

export async function httpGet<T>({
  callingMn,
  apiPath,
}: {
  callingMn: string
  apiPath: string
}) {
  const fn = `${callingMn} --> httpGet |`
  if (!apiPath) {
    throw new Error(`${fn} called with falsey apiPath`)
  }
  const url = `${apiPath}`
  console.debug(`${fn} Sending request GET ${url}.`, new Date().toISOString())
  const headers: Record<string, string> = Object.assign(
    { 'x-request-id': newRequestId() },
    userAuthHeader()
  )
  console.debug(`${fn} Request Id: ${headers['x-request-id']}`)

  // version
  headers['x-app-version'] = getPublicRuntimeConfig().version

  // A transport failure (offline, DNS, reset, CORS, dyno restart) rejects here
  // as a typed NetworkError; HTTP status codes > 300 are NOT network errors and
  // still arrive as a resolved response for the switch below.
  const response = await fetchOrThrowNetworkError(url, {
    method: 'GET',
    headers,
  })
  switch (response.status) {
    case HTTPStatus.OK:
      const responseBody: DataResponse<T> = await response.json()
      console.debug(`${fn} ------------> Response as object:`, responseBody)
      return responseBody.data
    // case HTTPStatus.InternalServerError:
    // case HTTPStatus.NotImplemented:
    // case HTTPStatus.BadGateway:
    // case HTTPStatus.GatewayTimeout:
    // case HTTPStatus.ServiceUnavailable:
    //   console.error(`${fn} ------------> Error response as object:`, response)
    //   throw new errors.ServiceError(response.status, message)
    case HTTPStatus.Forbidden:
      const error = new errors.ServiceError(
        response.status,
        response.statusText
      )
      // A 403 means the session/token is no longer valid. handleAsyncDataErrors
      // centrally handles this (notify + logout + redirect), so await it, then
      // throw the 403 ServiceError. The throw both preserves the existing
      // caller contract (callers expect data or a throw) and stops this case
      // falling through into `default`, which would otherwise ALSO throw
      // APIUnhandledStatus and double-handle the same 403.
      // The caller's own catch often hands this same error to
      // handleAsyncDataErrors again; notifySessionExpired is what keeps that
      // from showing the user a second "session expired".
      await handleAsyncDataErrors({
        callingMn: fn,
        error,
        context: getNuxtContext(), // Is this too ugly? We could just call logout directly rather than involving the nuxtian stuff. JR.
      })
      throw error
    // A read should never conflict, so this is defensive rather than expected —
    // but the point of typing 409 is that NO verb can leak one as a bare
    // "Conflict" again, so the seam is uniform across all four.
    case HTTPStatus.Conflict:
      throw new errors.UpdateConflictError(
        'GET',
        url,
        await readErrorBody(response)
      )
    default: {
      console.error(`${fn} ------------> Error response as object:`, response)
      // This nested { statusCode, message } shape is the CORRECT one, and the
      // other verbs were normalised toward it rather than the reverse:
      // handleAsyncDataErrors reads `error.error ? error.error.message : ...`,
      // so once `message` is a real string the error page shows the reason.
      const { message, body } = await parseErrorResponse(response)
      throw new errors.APIUnhandledStatus(response.status, {
        statusCode: response.status,
        message,
        body,
      } as unknown as IServiceError)
    }
  }
}
